/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import { StorageWrapper } from './storage-wrapper';
import { ProductSections } from '../enums';

import type { CompanyPrefixes } from '../../../enums';

export class TokenStorage<Token> {

    protected readonly companyPrefix: CompanyPrefixes;

    protected readonly localStorage: StorageWrapper;
    protected readonly sessionStorage: StorageWrapper;

    protected lastActiveTimeUpdateCallback: Function | null = null;
    protected tokenUpdateCallback: Function | null = null;

    constructor(companyPrefix: CompanyPrefixes) {
        this.companyPrefix = companyPrefix;

        this.localStorage = new StorageWrapper(localStorage);
        this.sessionStorage = new StorageWrapper(sessionStorage);

        this.initStorage();
    }

    getToken(): Token | null {
        const tokenStr: string | null = this.getStorage().getItem(this.getTokenFullKeyPrefix());
        const oldTokenStr: string | null = this.getStorage().getItem('vspc-token'); // @TODO Remove when all products move to shared

        if (tokenStr || oldTokenStr) {
            try {
                const token = JSON.parse(tokenStr || oldTokenStr);

                if (oldTokenStr && !tokenStr && token) { // @TODO Remove when all products move to shared
                    this.setToken(token);
                }

                return token;
            } catch (err) {
                this.removeToken();
                return null;
            }
        }

        return null;
    }

    setToken(tokenResponse: Token): void {
        this.setTokenItem(this.getTokenFullKeyPrefix(), JSON.stringify(tokenResponse));

        if (typeof this.tokenUpdateCallback === 'function') {
            this.tokenUpdateCallback();
        }
    }

    removeToken(): void {
        this.getStorage().removeItem(this.getTokenFullKeyPrefix());

        if (typeof this.tokenUpdateCallback === 'function') {
            this.tokenUpdateCallback();
        }
    }

    getLastActiveTime(): number | null {
        return this.getTokenItemAsNumber(this.getLastActiveTimeFullKeyPrefix());
    }

    setLastActiveTime(lastActiveTime: number): void {
        this.setTokenItem(this.getLastActiveTimeFullKeyPrefix(), `${lastActiveTime}`);

        if (typeof this.lastActiveTimeUpdateCallback === 'function') {
            this.lastActiveTimeUpdateCallback();
        }
    }

    getSessionExpirationTime(): number | null {
        return this.getTokenItemAsNumber(this.getSessionExpirationTimeFullKeyPrefix());
    }

    setSessionExpirationTime(sessionExpirationTime: number): void {
        this.setTokenItem(this.getSessionExpirationTimeFullKeyPrefix(), `${sessionExpirationTime}`);
    }

    getStorage(): StorageWrapper {
        return this.localStorage.isSupported ? this.localStorage : this.sessionStorage;
    }

    isStoragesEnabled(): boolean {
        return this.localStorage.isSupported || this.sessionStorage.isSupported;
    }

    onLastActiveTimeUpdate(callback: Function): void {
        this.lastActiveTimeUpdateCallback = callback;
    }

    onTokenUpdate(callback: Function): void {
        this.tokenUpdateCallback = callback;
    }

    protected getTokenItemAsNumber(key: string): number | null {
        const value: string | null = this.getStorage().getItem(key);

        return value ? Number.parseInt(value, 10) || null : null;
    }

    protected setTokenItem(key: string, value: string): void {
        const storage: StorageWrapper = this.getStorage();

        try {
            storage.setItem(key, value);
        } catch (err) {
            if ((err as Error).name === 'QuotaExceededError') {
                this.clearKeysInStorage();
                storage.setItem(key, value);
            }
        }
    }

    protected getTokenFullKeyPrefix(): string {
        return `${this.getGeneralKeyPrefix()}/${ProductSections.Token}`;
    }

    protected getLastActiveTimeFullKeyPrefix(): string {
        return `${this.getGeneralKeyPrefix()}/${ProductSections.LastActiveTime}`;
    }

    protected getSessionExpirationTimeFullKeyPrefix(): string {
        return `${this.getGeneralKeyPrefix()}/${ProductSections.SessionExpirationTime}`;
    }

    protected getGeneralKeyPrefix(): string {
        return `${this.companyPrefix}/${ProductSections.General}`;
    }

    protected clearKeysInStorage(): void {
        const excludeKeys: string[] = [
            this.getGeneralKeyPrefix(),
        ];
        this.getStorage().removeMultipleItems('', excludeKeys);
    }

    protected initStorage(): void {
        window.addEventListener('storage', (event) => {
            if (event.key === this.getLastActiveTimeFullKeyPrefix() && typeof this.lastActiveTimeUpdateCallback === 'function') {
                this.lastActiveTimeUpdateCallback();
            } else if (event.key === this.getTokenFullKeyPrefix() && typeof this.tokenUpdateCallback === 'function') {
                this.tokenUpdateCallback();
            }
        });
    }
}
